/**
 * Role-based routing of media-element events for the dual-element crossfade
 * mixer (see audio-mixer.ts).
 *
 * In crossfade mode playback alternates between two HTMLAudioElements, so the
 * playback hook attaches the SAME listener set to both elements exactly once
 * and lets this module decide, per event and per element role at dispatch
 * time, whether the event is meaningful:
 *
 * - "active"   — the element currently on air (the mixer's activeEl()).
 * - "incoming" — the element being faded in by an in-flight crossfade (it is
 *   already loading/playing before the mixer flips `active` at fade end).
 * - "idle"     — the other element: just faded out (paused, src removed) or
 *   never used. Events it fires (e.g. an aborted load after a src reset)
 *   are teardown noise and must not reach the engine or failure budget.
 *
 * Pure and DOM-free so it is unit-testable under Node strip-types.
 */

export type MediaElementRole = "active" | "incoming" | "idle";

export const ROUTED_MEDIA_EVENTS = [
  "ended",
  "timeupdate",
  "pause",
  "error",
  "play",
  "loadedmetadata",
] as const;

export type RoutedMediaEvent = (typeof ROUTED_MEDIA_EVENTS)[number];

/**
 * Policy: which element roles may deliver which events.
 *
 * - `ended`/`timeupdate`/`pause` drive the advance loop, position UI and
 *   telemetry — only the on-air element may speak, otherwise the outgoing
 *   element's teardown `pause` (and a stale `ended`) would stop or double-run
 *   the engine.
 * - `error` must also be accepted from the incoming crossfade leg: a track
 *   that fails to load mid-fade has to hit the failure budget and advance,
 *   not silently vanish. Idle-element errors are ignored.
 * - `play`/`loadedmetadata` reset the failure budget/backoff, and the
 *   incoming element is the one that loads and starts during a crossfade, so
 *   both active and incoming count as healthy playback.
 */
export function shouldProcessMediaEvent(
  event: RoutedMediaEvent,
  role: MediaElementRole,
): boolean {
  switch (event) {
    case "ended":
    case "timeupdate":
    case "pause":
      return role === "active";
    case "error":
    case "play":
    case "loadedmetadata":
      return role === "active" || role === "incoming";
  }
}

/** Minimal element surface the router needs (structural, so tests can fake it). */
export interface MediaEventTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/** Handlers receive the element the event fired on (not a closed-over one). */
export type MediaEventHandlers<T extends MediaEventTarget> = Partial<
  Record<RoutedMediaEvent, (el: T) => void>
>;

export interface MediaEventRouter<T extends MediaEventTarget> {
  /** Attach one guarded listener per handled event to `el`. */
  attach(el: T): void;
  /** Remove every listener this router added, on every attached element. */
  detachAll(): void;
}

/**
 * Create a router that attaches guarded listeners: each dispatch re-reads the
 * element's role via `getRole` (roles change when the mixer flips `active`),
 * applies `shouldProcessMediaEvent`, and forwards to the handler with the
 * event's element so handlers read currentTime/duration/error from the right
 * source.
 */
export function createMediaEventRouter<T extends MediaEventTarget>(
  getRole: (el: T) => MediaElementRole,
  handlers: MediaEventHandlers<T>,
): MediaEventRouter<T> {
  const bound: Array<{ el: T; type: RoutedMediaEvent; listener: () => void }> =
    [];
  return {
    attach(el: T): void {
      for (const type of ROUTED_MEDIA_EVENTS) {
        const handler = handlers[type];
        if (!handler) continue;
        const listener = () => {
          if (shouldProcessMediaEvent(type, getRole(el))) handler(el);
        };
        el.addEventListener(type, listener);
        bound.push({ el, type, listener });
      }
    },
    detachAll(): void {
      for (const { el, type, listener } of bound) {
        el.removeEventListener(type, listener);
      }
      bound.length = 0;
    },
  };
}
